<template>
  <div class="comment-action-overlay">
    <div class="action-overlay" v-show="commentEditContainerDisplay">
        <div class="container">
          <div class="item">
            <mentionable
              :keys="data.keys"
              :items="data.items"
              :limit="data.mentionLimit"
              insert-space>
              <textarea placeholder="@名前を入力してください" v-model="data.text" ref="area" :style="styles"></textarea>
              <template #no-result>
                <div class="dim">
                  No result
                </div>
              </template>
              <template #item-@="{ item }">
                <div class="destination" v-if="!item.groupFlg">
                  {{ item.lastName + item.firstName }}
                  <span class="dim">
                    {{ item.nickname }}
                  </span>
                </div>
                <div class="group" v-else>
                  {{ item.name }}
                  <span class="dim">
                    ({{ item.count }}名)
                  </span>
                </div>
              </template>
            </mentionable>
          </div>
          <div class="action">
            <button class="cancel" @click="cancel">キャンセル</button>
            <button class="confirm" @click="save">保存</button>
          </div>
        </div>
    </div>
  </div>
</template>
<script setup>
import axios from 'axios'
import Global from '@/js/constant.js'
import mentionUtils from '@/js/mention-utils.js'
import _ from 'lodash'

import { useStore } from 'vuex'
import { useToast } from 'vue-toast-notification'
import { computed, nextTick, reactive, useTemplateRef, watch } from 'vue'

const $store = useStore()
const $toast = useToast()
const $refArea = useTemplateRef('area')

const data = reactive({
  parentEntryId: null,
  entry: null,  
  text: null,
  keys: ['@'],
  mentionLimit: Global.vue_mention_limit,
  items: [],
  accounts: null,
  groups: null,
  groupToAccounts: {},
  height: '70px'
})

// computed
const styles = computed(() => {
  return {
    "height": data.height,
  }
})

// mapState
const commentEditContainerDisplay = computed(() => $store.state.entry.commentEditContainerDisplay)
const commentEditContainerContext = computed(() => $store.state.entry.commentEditContainerContext)
const allAccounts = computed(() => $store.state.allAccounts.accounts)
const circles = computed(() => $store.state.circles.circles)
const followers = computed(() => $store.state.follows.followers)

// mapMutations
const setCommentReloadEntryId = (val) => $store.commit('setCommentReloadEntryId',val)
const setCommentEditContainerDisplay = (val) => $store.commit('setCommentEditContainerDisplay',val)
const initCommentEditContainerContext = (val) => $store.commit('initCommentEditContainerContext',val)
const setClientErrors = (val) => $store.commit('setClientErrors',val)

// watch
watch(commentEditContainerContext, (newCommentEditContext) => {
  if (!newCommentEditContext.entry) {
    data.entry = null
    data.text = null
    data.items = []
    return
  }
  data.parentEntryId = newCommentEditContext.parentEntryId
  data.entry = newCommentEditContext.entry
  data.text = newCommentEditContext.entry.text
  // メンションに出すアカウント候補取得
  data.items = getMentionDestinations(newCommentEditContext)
  // テキストよりグループ文字列（#グループ：１）を作成
  if (newCommentEditContext.entry.entry_division_id == Global.entry_division_id.circle 
  || newCommentEditContext.entry_division_id == Global.entry_division_id.private_circle) {
    data.text = mentionUtils.setGroupIdsToText(data.text, data.groups)
  }

  if (data.items.length > 0) {
    data.items = data.items.concat(mentionUtils.getHereState())
  }
})
watch(() => data.text, () => {
  resize()
})

// methods
const getMentionDestinations = (newCommentEditContext) => {
  if (newCommentEditContext.entry.entry_division_id == Global.entry_division_id.square) {
    data.accounts = allAccounts.value
    return data.accounts.map(row => mentionUtils.getMentionStates(row))
  }
  if (newCommentEditContext.entry.entry_division_id == Global.entry_division_id.circle 
    || newCommentEditContext.entry.entry_division_id == Global.entry_division_id.private_circle) {
    let filterCircle = _.keyBy(circles.value, 'id')[newCommentEditContext.entry.circle_id]
    data.accounts = filterCircle.members
    data.groups = filterCircle.groups
    data.groupToAccounts = mentionUtils.createGroupMentionToMembersMentionConverter(data.groups)
    let res = filterCircle.members.map(r => mentionUtils.getMentionStates(r))
    return res.concat(filterCircle.groups.map(r => mentionUtils.getMentionGroupsStates(r)))
  }
  if (newCommentEditContext.entry.entry_division_id == Global.entry_division_id.my_space) {
    data.accounts = followers.value.map(r => r.follow_account)
    return data.accounts.map(r => mentionUtils.getMentionStates(r))
  }
  return []
}
const cancel = () => {
  setCommentEditContainerDisplay(false);
  initCommentEditContainerContext();
}
const save = async () => {
  data.text = mentionUtils.changeGroupToMembers(data.text, data.groupToAccounts)
  let destinationIds = mentionUtils.getDestinationIdsFromText(data.text, data.accounts, [])
  let destinationGroupIds = mentionUtils.getGroupIdsFromText(data.text, data.groups)
  
  var formData = new FormData()
  formData.append("insert_flg", Global.OFF)
  formData.append("id", data.entry.id)
  formData.append("text", mentionUtils.deleteAccountIdFromText(data.text, data.accounts, []))
  formData.append("destination_ids", destinationIds)
  formData.append("destination_group_ids", destinationGroupIds)
  formData.append("all_dest_flg", mentionUtils.getAllDestFlg(data.text))
  formData.append("version_no", data.entry.version_no)

  await axios.post(Global.url.api_entries_tweet, formData).then(res => {
    if (res.data.status == Global.api_status.success) {
      data.text = ""
      setCommentReloadEntryId(data.parentEntryId)
      setCommentEditContainerDisplay(false)
      initCommentEditContainerContext()
      $toast.info("つぶやきました")
      return
    }
    $toast.error("failed")
  })
  .catch(error=>{
    $toast.error('client error')
    setClientErrors({func:"common/comment-edit.vue::save", params: formData, errorMessage:error})
  })
}
const resize = () => {
  if (!$refArea.value?.scrollHeight) return
  data.height = $refArea.value.scrollHeight + 'px'
  data.height = "auto"
  nextTick(() => {
    data.height = $refArea.value.scrollHeight + 'px'
  })
}
</script>